import React, { Component, Fragment } from "react";
import { Icon } from "@iconify/react";
import {
  ClearSessionItems,
  LocalStorage,
  getCachedLocation,
} from "../helper/appsession";
import { axiosPost, axiosGet } from "../apis/useAxios";
import { LoginAssetType } from "../helper/fixcode";
import ChangePasswordComponent from "../components/Shared/ChangePasswordComponent";
import * as moment from "moment";
import * as signalR from "@microsoft/signalr";
// Toast CSS injected once
if (typeof document !== "undefined" && !document.getElementById("wti-toast-css")) {
  const s = document.createElement("style");
  s.id = "wti-toast-css";
  s.textContent = `
    @keyframes wtiSlideIn { from { opacity:0; transform:translateX(40px); } to { opacity:1; transform:translateX(0); } }
    @keyframes wtiShrink  { from { width:100%; } to { width:0%; } }
    .wti-toast-item { animation: wtiSlideIn 0.25s ease; }
    .wti-toast-bar  { animation: wtiShrink 5s linear forwards; }
  `;
  document.head.appendChild(s);
}

const _TOAST_COLORS = { info: "#0d6efd", success: "#198754", error: "#dc3545", warning: "#e67e22" };
const _TOAST_ICONS  = { info: "ℹ️", success: "✅", error: "❌", warning: "⚠️" };

function WtiToast({ id, title, message, type, onDismiss }) {
  const color = _TOAST_COLORS[type] || _TOAST_COLORS.info;
  const icon  = _TOAST_ICONS[type]  || _TOAST_ICONS.info;
  return (
    <div className="wti-toast-item" style={{
      background: "#fff", border: "1px solid #e2e8f0",
      borderLeft: `4px solid ${color}`, borderRadius: 8,
      boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
      padding: "12px 16px", width: 300,
      display: "flex", alignItems: "flex-start", gap: 10,
      pointerEvents: "all",
    }}>
      <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: "#2d3748", marginBottom: 2 }}>{title}</div>
        {message ? <div style={{ fontSize: 12, color: "#718096", lineHeight: 1.4 }}>{message}</div> : null}
        <div className="wti-toast-bar" style={{
          height: 3, background: color, borderRadius: "0 0 0 4px",
          margin: "8px -16px -12px -16px",
        }} />
      </div>
      <button onClick={onDismiss} style={{
        background: "none", border: "none", fontSize: 14,
        color: "#aaa", cursor: "pointer", flexShrink: 0, padding: 0, marginTop: 1,
      }}>✕</button>
    </div>
  );
}

const TIME_FORMAT = "ddd DD, MMM h:mm:ss A";
const NOTIF_POLL_MS = 30000;

export class TopBarComponent extends Component {
  static displayName = TopBarComponent.name;

  constructor(props) {
    super(props);
    this.state = {
      changePassword: {
        renderChangePasswordModal: false,
        userId: 0,
        userName: "",
      },
      currentTime: moment().format(TIME_FORMAT),
      unreadCount: 0,
      recentNotifications: [],
      notifDropdownOpen: false,
      toasts: [],
    };
    this._notifTimer = null;
    this._notifRef = React.createRef();
  }

  showToast = (title, message, type = "info") => {
    const id = Date.now() + Math.random();
    this.setState((prev) => ({ toasts: [...prev.toasts, { id, title, message, type }] }));
    setTimeout(() => this.dismissToast(id), 5000);
  };

  dismissToast = (id) => {
    this.setState((prev) => ({ toasts: prev.toasts.filter((t) => t.id !== id) }));
  };

  _handleOutsideClick = (e) => {
    if (this._notifRef.current && !this._notifRef.current.contains(e.target)) {
      this.setState({ notifDropdownOpen: false });
    }
  };

  async componentDidMount() {
    setInterval(() => {
      this.setState({ currentTime: moment().format(TIME_FORMAT) });
    }, 1000);
    document.addEventListener("mousedown", this._handleOutsideClick);

    // Initial fetch first — this ensures the API token is set before SignalR connects
    await this.fetchNotifications();
    this._notifTimer = setInterval(() => this.fetchNotifications(), NOTIF_POLL_MS);

    // SignalR real-time push — start after first fetch so token is guaranteed in localStorage
    const hubUrl = `${(process.env.REACT_APP_API_URL || "").replace(/\/$/, "")}/hubs/notifications`;
    this._signalrConn = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => localStorage.getItem("authorize_api_token") || "",
        // token auth, no cookies: the browser accepts the API's wildcard CORS header (Prod) only
        // for requests sent without credentials
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.None)
      .build();

    this._signalrConn.on("notification", (data) => {
      this.fetchNotifications(true); // true = SignalR already shows toast, skip polling toast
      const title   = (data && data.title)   || "New Notification";
      const message = (data && data.message) || "";
      this.showToast(title, message, "info");
    });

    this._signalrConn.start().catch(() => {
      // Hub unavailable — polling fallback still active
    });
  }

  componentWillUnmount() {
    clearInterval(this._notifTimer);
    if (this._signalrConn) this._signalrConn.stop();
    document.removeEventListener("mousedown", this._handleOutsideClick);
  }

  fetchNotifications = async (showToast = false) => {
    try {
      const prevCount = this.state.unreadCount;
      const [countRes, listRes] = await Promise.all([
        axiosGet("workflow/notifications/unread-count", { userId: LocalStorage.UserId }),
        axiosGet("workflow/notifications", { userId: LocalStorage.UserId, page: 1, pageSize: 5 }),
      ]);
      const newCount = countRes?.payload || 0;
      const notifications = listRes?.payload || [];

      // Show toast when polling detects new notifications (and SignalR didn't already fire one)
      if (!showToast && newCount > prevCount && prevCount > 0) {
        const latest = notifications[0];
        if (latest) {
          this.showToast(latest.title || "New Notification", latest.message || "", "info");
        }
      }

      this.setState({
        unreadCount: newCount,
        recentNotifications: notifications,
      });
    } catch (e) {
      // silent — TopBar should never break on notif error
    }
  };

  handleMarkAllRead = async (e) => {
    e.preventDefault();
    try {
      await axiosPost("workflow/notifications/mark-all-read", { userId: LocalStorage.UserId });
      this.setState({ unreadCount: 0 });
      this.fetchNotifications();
    } catch (e) { console.log(e); }
  };

  handleOnLogout = async (e) => {
    e.preventDefault();
    try {
      const { lat, lng } = getCachedLocation();
      await axiosPost(
        `/authenticate/logout?userId=${LocalStorage.UserId}`,
        { SessionId: LocalStorage.SessionId, AssetTypeId: LoginAssetType.Web, Latitude: lat, Longitude: lng },
        { timeout: 3000 },
      );
    } catch (error) {
      // logout error — session cleared below regardless
    }
    const wasSsoLogin = sessionStorage.getItem("session_sso_login") === "true";
    ClearSessionItems();
    window.location.href = wasSsoLogin
      ? `${process.env.REACT_APP_PORTAL_URL || ""}/logout`
      : "/";
  };
  handleOnCloaseChangePasswordModel = () => {
    try {
      let changePassword = this.state.changePassword;
      changePassword.renderChangePasswordModal = false;
      changePassword.userId = 0;
      changePassword.userName = "";
      this.setState((prev) => ({ ...prev, changePassword: changePassword }));
    } catch (error) {
      console.log(error);
    }
  };
  // Never let the menu toggle navigate to "#" (that re-renders the route and can blank the page).
  // AdminLTE's PushMenu toggles the sidebar when its script has loaded; otherwise toggle it here.
  handleToggleSidebar = (e) => {
    e.preventDefault();
    if (!window.jQuery?.fn?.PushMenu) {
      document.body.classList.toggle("sidebar-collapse");
    }
  };
  render() {
    return (
      <Fragment>
        <nav className="main-header navbar navbar-expand navbar-white navbar-light">
          <ul className="navbar-nav">
            <li className="nav-item">
              <a
                className="nav-link"
                data-widget="pushmenu"
                href="#"
                role="button"
                onClick={this.handleToggleSidebar}
              >
                {/* <img src="dist/img/menu-icon.svg" width={28} /> */}
                <Icon
                  icon="material-symbols:menu-open-rounded"
                  width="26"
                  height="26"
                />
              </a>
            </li>
            <li className="nav-item">
              <h5 className="mb-0" id="lblPageTitle"></h5>
            </li>
          </ul>
          <ul className="navbar-nav ml-auto">
            {/* <li>
              <a
                className="nav-link"
                data-widget="fullscreen"
                href="/ameyotoolbar"
                role="button"
              >
                <Icon
                  icon="zondicons:dial-pad"
                  width="18"
                  height="18"
                  className="btn-link"
                />
              </a>
            </li> */}
            <li className="d-none d-lg-block d-xl-block">
              <small>
                Welcome <b>{LocalStorage.UserName}!</b>
              </small>
            </li>
            <li className="d-none  d-lg-block  d-xl-block">
              <small>
                &nbsp;&nbsp;Logged-in as <b>[{LocalStorage.UserTypeName}]</b>
              </small>
              &nbsp;&nbsp;&nbsp;&nbsp;
            </li>
            <li className="d-none  d-lg-block  d-xl-block">
              <i className="fas fa-calendar-day topbar-clockicon"></i>{" "}
              <span className="topbar-clock">{this.state.currentTime}</span>
            </li>
            <li className="nav-item">
              <a
                className="nav-link"
                data-widget="fullscreen"
                href="#"
                role="button"
              >
                <i className="fas fa-expand-arrows-alt"></i>
              </a>
            </li>
            {/* Notification Bell */}
            <li className="nav-item dropdown" ref={this._notifRef}>
              <a
                className="nav-link"
                data-toggle="dropdown"
                href="#"
                onClick={(e) => { e.preventDefault(); this.setState((prev) => ({ notifDropdownOpen: !prev.notifDropdownOpen })); }}
                style={{ position: "relative" }}
              >
                <i className="fas fa-bell" style={{ fontSize: 16 }} />
                {this.state.unreadCount > 0 && (
                  <span className="badge badge-danger navbar-badge" style={{ fontSize: 9, padding: "2px 5px" }}>
                    {this.state.unreadCount > 99 ? "99+" : this.state.unreadCount}
                  </span>
                )}
              </a>
              <div className={`dropdown-menu dropdown-menu-lg dropdown-menu-right ${this.state.notifDropdownOpen ? "show" : ""}`} style={{ minWidth: 320 }}>
                <div style={{ padding: "8px 14px", borderBottom: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <strong style={{ fontSize: 13 }}>Notifications</strong>
                  {this.state.unreadCount > 0 && (
                    <a href="#" style={{ fontSize: 11, color: "#0d6efd" }} onClick={this.handleMarkAllRead}>Mark all read</a>
                  )}
                </div>
                {this.state.recentNotifications.length === 0 ? (
                  <div style={{ padding: "16px", textAlign: "center", color: "#888", fontSize: 12 }}>
                    No notifications
                  </div>
                ) : (
                  this.state.recentNotifications.map((n) => (
                    <a
                      key={n.id}
                      className="dropdown-item"
                      href={/^\/(?![/\\])/.test(n.linkUrl || "") ? n.linkUrl : "/my-inbox"}
                      style={{ borderBottom: "1px solid #f5f5f5", padding: "8px 14px", background: n.isRead ? "#fff" : "#F0F7FF" }}
                    >
                      <div style={{ fontWeight: n.isRead ? 400 : 600, fontSize: 12 }}>{n.title}</div>
                      <div style={{ fontSize: 11, color: "#888", marginTop: 2, whiteSpace: "normal" }}>{n.message}</div>
                    </a>
                  ))
                )}
                <div style={{ textAlign: "center", padding: "8px 0", borderTop: "1px solid #eee" }}>
                  <a href="/my-inbox"
                    style={{ fontSize: 12, color: "#0d6efd" }} onClick={() => this.setState({ notifDropdownOpen: false })}>
                    View All in My Inbox →
                  </a>
                </div>
              </div>
            </li>

            <li className="nav-item dropdown">
              <a className="nav-link" data-toggle="dropdown" href="#">
                <Icon icon="tdesign:user" />
              </a>
              <div className="dropdown-menu dropdown-menu-lg dropdown-menu-right">
                <div className="media p-2">
                  <img
                    className="mr-3 usericon"
                    src="../dist/img/avatar.png"
                    alt="User Icon"
                  />
                  <div className="media-body">
                    <small>
                      Logged-in as <br />
                      <b>{LocalStorage.UserTypeName}</b>
                    </small>
                    <hr />
                    <p>
                      {LocalStorage.FirstName && (
                        <>
                          <strong>
                            {LocalStorage.FirstName} {LocalStorage.LastName}
                          </strong>
                          <br />
                        </>
                      )}
                      <small>
                        <Icon icon="fa:user" /> {LocalStorage.UserName}
                      </small>
                      {LocalStorage.Email && (
                        <>
                          <br />
                          <Icon icon="material-symbols:mail" />{" "}
                          <small>{LocalStorage.Email}</small>
                        </>
                      )}
                      {LocalStorage.PhoneNumber && (
                        <>
                          <br />
                          <Icon icon="fluent:phone-32-filled" />{" "}
                          <small>{LocalStorage.PhoneNumber}</small>
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <hr />
                <a
                  className="dropdown-item"
                  href="#"
                  onClick={(e) => this.handleOnLogout(e)}
                >
                  <Icon icon="material-symbols:logout" /> Logout{" "}
                </a>
                <a
                  className="dropdown-item"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    let changePassword = this.state.changePassword;
                    changePassword.renderChangePasswordModal = true;
                    changePassword.userId = LocalStorage.UserId;
                    changePassword.userName = LocalStorage.UserName;
                    this.setState((prev) => ({
                      ...prev,
                      changePassword: changePassword,
                    }));
                  }}
                >
                  <Icon icon="material-symbols:key" /> Change Password
                </a>
              </div>
            </li>
          </ul>
        </nav>
        {this.state.changePassword.renderChangePasswordModal && (
          <ChangePasswordComponent
            userId={this.state.changePassword.userId}
            userName={this.state.changePassword.userName}
            onClose={this.handleOnCloaseChangePasswordModel}
          />
        )}
        {/* Custom toast notifications */}
        <div style={{
          position: "fixed", top: 20, right: 20, zIndex: 9999,
          display: "flex", flexDirection: "column", gap: 10,
          pointerEvents: "none",
        }}>
          {this.state.toasts.map((t) => (
            <WtiToast key={t.id} {...t} onDismiss={() => this.dismissToast(t.id)} />
          ))}
        </div>
      </Fragment>
    );
  }
}
